import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Line, Bar, Pie, Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import consultantsImage from '../assets/images/Image 4_Consultants.png';
import SEO from '../components/SEO';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Add custom styles for enhanced interactions
const styles = `
  .feature-card {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    transform-style: preserve-3d;
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%);
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.5s forwards;
    border: 1px solid rgba(59, 130, 246, 0.1);
  }

  .feature-card:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 24px -10px rgba(66, 153, 225, 0.3);
    border-color: rgba(59, 130, 246, 0.2);
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(239, 246, 255, 0.9) 100%);
  }

  .chart-container {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    transition: all 0.3s ease;
  }

  .chart-container:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  .math-card {
    background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(239, 246, 255, 0.95) 100%);
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }

  .math-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }

  @keyframes fadeInUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

function evaluateSafeMathExpression(expression) {
  if (!/^[0-9+\-*/().]+$/.test(expression)) {
    throw new Error("Invalid expression");
  }
  let index = 0;

  const parseExpression = () => {
    let value = parseTerm();
    while (index < expression.length) {
      const op = expression[index];
      if (op !== '+' && op !== '-') break;
      index += 1;
      const rhs = parseTerm();
      value = op === '+' ? value + rhs : value - rhs;
    }
    return value;
  };

  const parseTerm = () => {
    let value = parseFactor();
    while (index < expression.length) {
      const op = expression[index];
      if (op !== '*' && op !== '/') break;
      index += 1;
      const rhs = parseFactor();
      value = op === '*' ? value * rhs : value / rhs;
    }
    return value;
  };

  const parseFactor = () => {
    if (expression[index] === '+') {
      index += 1;
      return parseFactor();
    }
    if (expression[index] === '-') {
      index += 1;
      return -parseFactor();
    }
    if (expression[index] === '(') {
      index += 1;
      const value = parseExpression();
      if (expression[index] !== ')') {
        throw new Error("Invalid expression");
      }
      index += 1;
      return value;
    }
    const match = expression.slice(index).match(/^\d+(?:\.\d+)?/);
    if (!match) {
      throw new Error("Invalid expression");
    }
    index += match[0].length;
    return Number(match[0]);
  };

  const value = parseExpression();
  if (index !== expression.length) {
    throw new Error("Invalid expression");
  }
  return value;
}

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default function ConsultingMathGuidesPage() {
  const navigate = useNavigate();
  const mathSectionRef = useRef(null);
  const [isTestSectionOpen, setIsTestSectionOpen] = useState(false);
  const [calculatorValue, setCalculatorValue] = useState('0');
  const [calculatorExpression, setCalculatorExpression] = useState('');
  const [calculatorHistory, setCalculatorHistory] = useState([]);
  const [error, setError] = useState('');
  const chartSectionRef = useRef(null);
  const [isChartTestSectionOpen, setIsChartTestSectionOpen] = useState(false);
  const [chartTestAnswers, setChartTestAnswers] = useState({});
  const [chartTestFeedback, setChartTestFeedback] = useState({});
  const [chartTestReveal, setChartTestReveal] = useState({});
  const [activeMathTest, setActiveMathTest] = useState(1);
  const [activeChartTest, setActiveChartTest] = useState(1);

  // Add chart data definitions
  const stackedBarTestData = {
    labels: ['Q1', 'Q2', 'Q3', 'Q4'],
    datasets: [
      {
        label: 'Product A',
        data: [40, 35, 45, 50],
        backgroundColor: 'rgba(59, 130, 246, 0.7)',
      },
      {
        label: 'Product B',
        data: [30, 40, 35, 45],
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
      },
      {
        label: 'Product C',
        data: [20, 25, 30, 35],
        backgroundColor: 'rgba(245, 158, 11, 0.7)',
      }
    ]
  };

  const histogramTestData = {
    labels: ['0-10', '11-20', '21-30', '31-40', '41-50', '51-60', '61-70', '71-80', '81-90', '91-100'],
    datasets: [{
      label: 'Test Scores Distribution',
      data: [5, 8, 12, 15, 20, 18, 12, 6, 3, 1],
      backgroundColor: 'rgba(59, 130, 246, 0.7)',
      borderColor: 'rgba(59, 130, 246, 1)',
      borderWidth: 1
    }]
  };

  // Add mathTest2Questions array
  const mathTest2Questions = [
    {
      id: 101,
      question: "What is 12.5% of 800?",
      correctAnswer: "100",
      explanation: "Using the formula: (12.5 ÷ 100) × 800 = 0.125 × 800 = 100"
    },
    {
      id: 102,
      question: "If a price decreases from $200 to $150, what is the percentage decrease?",
      correctAnswer: "25",
      explanation: "Using the formula: [(150 - 200) ÷ 200] × 100 = (-50 ÷ 200) × 100 = -25%"
    },
    {
      id: 103,
      question: "What is 3/4 + 1/8? (Answer in fraction form like '7/8')",
      correctAnswer: "7/8",
      explanation: "Convert 3/4 to 6/8, then add: 6/8 + 1/8 = 7/8"
    },
    {
      id: 104,
      question: "What is 2/3 × 3/4? (Answer in fraction form like '1/2')",
      correctAnswer: "1/2",
      explanation: "Multiply numerators and denominators: (2×3)/(3×4) = 6/12 = 1/2"
    },
    {
      id: 105,
      question: "If 2/3 = x/9, what is x?",
      correctAnswer: "6",
      explanation: "Cross-multiply: 2 × 9 = 3 × x → 18 = 3x → x = 6"
    }
  ];

  // Add chart test handler functions
  const handleChartTestSelect = (id, value) => {
    setChartTestAnswers(prev => ({ ...prev, [id]: value }));
    // Find the question in both sets, use the one that matches the id
    let question = chartTestQuestions.find(q => q.id === id);
    if (!question) {
      question = chartTest2Questions.find(q => q.id === id);
    }
    if (!question) return;
    
    // Normalize dashes and trim for robust comparison
    const normalize = str => String(str).replace(/[–—−]/g, '-').trim();
    const userAnswer = normalize(value);
    const correctAnswer = normalize(question.correctAnswer);
    const isCorrect = userAnswer === correctAnswer;
    
    setChartTestFeedback(prev => ({ ...prev, [id]: isCorrect ? 'correct' : 'incorrect' }));
  };

  const handleChartTestTryAgain = (id) => {
    setChartTestAnswers(prev => ({ ...prev, [id]: null }));
    setChartTestFeedback(prev => ({ ...prev, [id]: null }));
    setChartTestReveal(prev => ({ ...prev, [id]: false }));
  };

  const handleChartTestReveal = (id) => {
    setChartTestReveal(prev => ({ ...prev, [id]: true }));
  };

  // Sample data for charts
  const barChartData = {
    labels: ['Product A', 'Product B', 'Product C', 'Product D'],
    datasets: [
      {
        label: 'Sales Figures',
        data: [65, 59, 80, 81],
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Sales Figures by Product',
      },
    },
  };

  // Add more chart data
  const stackedBarData = {
    labels: ['Q1', 'Q2', 'Q3', 'Q4'],
    datasets: [
      {
        label: 'Product A',
        data: [30, 45, 35, 50],
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
      },
      {
        label: 'Product B',
        data: [20, 35, 25, 40],
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
      },
      {
        label: 'Product C',
        data: [15, 25, 20, 30],
        backgroundColor: 'rgba(245, 158, 11, 0.5)',
      },
    ],
  };

  // Add 100% Stacked Bar Chart data
  const stackedBar100Data = {
    labels: ['Region A', 'Region B', 'Region C', 'Region D'],
    datasets: [
      {
        label: 'Product A',
        data: [30, 45, 35, 50],
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
      },
      {
        label: 'Product B',
        data: [40, 35, 45, 30],
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
      },
      {
        label: 'Product C',
        data: [30, 20, 20, 20],
        backgroundColor: 'rgba(245, 158, 11, 0.5)',
      },
    ],
  };

  const pieChartData = {
    labels: ['Product A', 'Product B', 'Product C', 'Product D'],
    datasets: [{
      data: [30, 25, 20, 25],
      backgroundColor: [
        'rgba(59, 130, 246, 0.5)',
        'rgba(16, 185, 129, 0.5)',
        'rgba(245, 158, 11, 0.5)',
        'rgba(239, 68, 68, 0.5)',
      ],
      borderColor: [
        'rgba(59, 130, 246, 1)',
        'rgba(16, 185, 129, 1)',
        'rgba(245, 158, 11, 1)',
        'rgba(239, 68, 68, 1)',
      ],
      borderWidth: 1,
    }],
  };

  const lineChartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [{
      label: 'Monthly Revenue',
      data: [65, 59, 80, 81, 56, 55],
      fill: false,
      borderColor: 'rgba(59, 130, 246, 1)',
      tension: 0.1
    }]
  };

  const scatterData = {
    datasets: [{
      label: 'Study Hours vs Test Scores',
      data: [
        { x: 2, y: 65 },
        { x: 3, y: 70 },
        { x: 4, y: 75 },
        { x: 5, y: 80 },
        { x: 6, y: 85 },
        { x: 7, y: 90 },
      ],
      backgroundColor: 'rgba(59, 130, 246, 0.5)',
    }]
  };

  // Update test questions to 25 questions
  const testQuestions = [
    {
      id: 1,
      question: "What is 15% of 200?",
      correctAnswer: "30",
      explanation: "Using the formula: (15 ÷ 100) × 200 = 0.15 × 200 = 30"
    },
    {
      id: 2,
      question: "If a price increases from $80 to $100, what is the percentage increase?",
      correctAnswer: "25",
      explanation: "Using the formula: [(100 - 80) ÷ 80] × 100 = (20 ÷ 80) × 100 = 25%"
    },
    {
      id: 3,
      question: "What is 2/3 + 1/6? (Answer in fraction form like '5/6')",
      correctAnswer: "5/6",
      explanation: "First convert 2/3 to 4/6, then add: 4/6 + 1/6 = 5/6"
    },
    {
      id: 4,
      question: "What is 3/4 × 2/5? (Answer in fraction form like '3/10')",
      correctAnswer: "3/10",
      explanation: "Multiply numerators and denominators: (3×2)/(4×5) = 6/20 = 3/10"
    },
    {
      id: 5,
      question: "If 3/4 = x/12, what is x?",
      correctAnswer: "9",
      explanation: "Cross-multiply: 3 × 12 = 4 × x → 36 = 4x → x = 9"
    },
    {
      id: 6,
      question: "What is the average of 15 and 25?",
      correctAnswer: "20",
      explanation: "Using the formula: (15 + 25)/2 = 40/2 = 20"
    },
    {
      id: 7,
      question: "If you have a 30% chance of rain on Monday and 40% on Tuesday, what's the probability of rain on at least one day? (Answer as a percentage)",
      correctAnswer: "58",
      explanation: "Using the formula: 1 - P(none) = 1 - (0.7 × 0.6) = 1 - 0.42 = 0.58 = 58%"
    },
    {
      id: 8,
      question: "What is 5/8 ÷ 2/3? (Answer in fraction form like '15/16')",
      correctAnswer: "15/16",
      explanation: "Multiply by reciprocal: 5/8 × 3/2 = 15/16"
    },
    {
      id: 9,
      question: "If a stock price drops from $120 to $90, what is the percentage decrease?",
      correctAnswer: "25",
      explanation: "Using the formula: [(90 - 120) ÷ 120] × 100 = (-30 ÷ 120) × 100 = -25%"
    },
    {
      id: 10,
      question: "What is the weighted average of 80 (weight 2) and 90 (weight 3)?",
      correctAnswer: "86",
      explanation: "Using the formula: [(80×2) + (90×3)] ÷ (2+3) = (160 + 270) ÷ 5 = 430 ÷ 5 = 86"
    },
    {
      id: 11,
      question: "What is 12.5% of 400?",
      correctAnswer: "50",
      explanation: "Using the formula: (12.5 ÷ 100) × 400 = 0.125 × 400 = 50"
    },
    {
      id: 12,
      question: "If a price decreases from $150 to $120, what is the percentage decrease?",
      correctAnswer: "20",
      explanation: "Using the formula: [(120 - 150) ÷ 150] × 100 = (-30 ÷ 150) × 100 = -20%"
    },
    {
      id: 13,
      question: "What is 1/3 + 1/4? (Answer in fraction form like '7/12')",
      correctAnswer: "7/12",
      explanation: "Convert to common denominator: 4/12 + 3/12 = 7/12"
    },
    {
      id: 14,
      question: "What is 2/5 × 3/7? (Answer in fraction form like '6/35')",
      correctAnswer: "6/35",
      explanation: "Multiply numerators and denominators: (2×3)/(5×7) = 6/35"
    },
    {
      id: 15,
      question: "If 2/5 = x/15, what is x?",
      correctAnswer: "6",
      explanation: "Cross-multiply: 2 × 15 = 5 × x → 30 = 5x → x = 6"
    },
    {
      id: 16,
      question: "What is the average of 18, 22, and 26?",
      correctAnswer: "22",
      explanation: "Using the formula: (18 + 22 + 26)/3 = 66/3 = 22"
    },
    {
      id: 17,
      question: "If you have a 25% chance of success on first try and 40% on second try, what's the probability of success on at least one try? (Answer as a percentage)",
      correctAnswer: "55",
      explanation: "Using the formula: 1 - P(none) = 1 - (0.75 × 0.6) = 1 - 0.45 = 0.55 = 55%"
    },
    {
      id: 18,
      question: "What is 3/4 ÷ 1/2? (Answer in fraction form like '3/2')",
      correctAnswer: "3/2",
      explanation: "Multiply by reciprocal: 3/4 × 2/1 = 6/4 = 3/2"
    },
    {
      id: 19,
      question: "If a stock price increases from $80 to $100, what is the percentage increase?",
      correctAnswer: "25",
      explanation: "Using the formula: [(100 - 80) ÷ 80] × 100 = (20 ÷ 80) × 100 = 25%"
    },
    {
      id: 20,
      question: "What is the weighted average of 70 (weight 3) and 90 (weight 2)?",
      correctAnswer: "78",
      explanation: "Using the formula: [(70×3) + (90×2)] ÷ (3+2) = (210 + 180) ÷ 5 = 390 ÷ 5 = 78"
    },
    {
      id: 21,
      question: "What is 7.5% of 200?",
      correctAnswer: "15",
      explanation: "Using the formula: (7.5 ÷ 100) × 200 = 0.075 × 200 = 15"
    },
    {
      id: 22,
      question: "What is 1/2 + 1/3? (Answer in fraction form like '5/6')",
      correctAnswer: "5/6",
      explanation: "Convert to common denominator: 3/6 + 2/6 = 5/6"
    },
    {
      id: 23,
      question: "What is 3/5 × 4/7? (Answer in fraction form like '12/35')",
      correctAnswer: "12/35",
      explanation: "Multiply numerators and denominators: (3×4)/(5×7) = 12/35"
    },
    {
      id: 24,
      question: "If 4/5 = x/20, what is x?",
      correctAnswer: "16",
      explanation: "Cross-multiply: 4 × 20 = 5 × x → 80 = 5x → x = 16"
    },
    {
      id: 25,
      question: "What is the average of 12, 15, 18, and 21?",
      correctAnswer: "16.5",
      explanation: "Using the formula: (12 + 15 + 18 + 21)/4 = 66/4 = 16.5"
    }
  ];

  // Enhanced calculator functions
  const handleCalculatorInput = (value) => {
    if (calculatorValue !== calculatorExpression) {
      setCalculatorExpression(value);
      setCalculatorValue(value);
    } else {
      setCalculatorExpression(prev => prev + value);
      setCalculatorValue(prev => prev + value);
    }
    setError('');
  };

  const handleCalculatorOperation = (operation) => {
    // Prevent two operators in a row
    if (['+', '-', '×', '÷'].includes(calculatorExpression.slice(-1))) {
      setCalculatorExpression(prev => prev.slice(0, -1) + operation);
      setCalculatorValue(prev => prev.slice(0, -1) + operation);
    } else {
      setCalculatorExpression(prev => prev + operation);
      setCalculatorValue(prev => prev + operation);
    }
  };

  const handleCalculatorBracket = (type) => {
    setCalculatorExpression(prev => prev + type);
    setCalculatorValue(prev => prev + type);
  };

  const handleCalculatorFraction = () => {
    setCalculatorExpression(prev => prev + '÷');
    setCalculatorValue(prev => prev + '÷');
  };

  const handleCalculatorPercentage = () => {
    setCalculatorExpression(prev => prev + '%');
    setCalculatorValue(prev => prev + '%');
  };

  const handleCalculatorEquals = () => {
    try {
      let sanitizedExpression = calculatorExpression
        // Replace all possible multiplication/division symbols with JS operators
        .replace(/[×✕✖✗✘]/g, '*')
        .replace(/[÷∕⁄／]/g, '/')
        .replace(/%/g, '/100')
        .replace(/\s+/g, '');

      // Validate after all replacements
      if (!/^[0-9+\-*/().]+$/.test(sanitizedExpression)) {
        // console.error('Sanitized expression failed validation:', sanitizedExpression);
        setError('Invalid expression. Please check your input.');
        return;
      }

      const result = evaluateSafeMathExpression(sanitizedExpression);

      if (isNaN(result) || !isFinite(result)) {
        throw new Error('Invalid calculation');
      }
      const formattedResult = Number.isInteger(result) ? result.toString() : result.toFixed(2);
      setCalculatorValue(formattedResult);
      setCalculatorHistory(prev => [...prev, {
        expression: calculatorExpression,
        result: formattedResult
      }]);
    } catch (error) {
      // console.error('Calculator error:', error);
      setError('Calculation failed. Please check your expression.');
    }
  };

  const handleCalculatorClear = () => {
    setCalculatorValue('0');
    setCalculatorExpression('');
    setError('');
  };

  const handleCalculatorBackspace = () => {
    if (calculatorExpression.length > 1) {
      setCalculatorExpression(prev => prev.slice(0, -1));
      setCalculatorValue(prev => prev.slice(0, -1));
    } else {
      setCalculatorValue('0');
      setCalculatorExpression('');
    }
  };

  // Add new state for test questions
  const [testAnswers, setTestAnswers] = useState({});
  const [testFeedback, setTestFeedback] = useState({});
  const [testReveal, setTestReveal] = useState({});

  // Add test question handlers
  const handleTestInput = (id, value) => {
    setTestAnswers(prev => ({ ...prev, [id]: value }));
  };

  const handleTestSubmit = (id) => {
    const question = testQuestions.find(q => q.id === id);
    const userAnswer = (testAnswers[id] || '').trim().toLowerCase();
    const correctAnswer = question.correctAnswer.toLowerCase();
    setTestFeedback(prev => ({ ...prev, [id]: userAnswer === correctAnswer ? 'correct' : 'incorrect' }));
  };

  const handleTestTryAgain = (id) => {
    setTestAnswers(prev => ({ ...prev, [id]: '' }));
    setTestFeedback(prev => ({ ...prev, [id]: null }));
    setTestReveal(prev => ({ ...prev, [id]: false }));
  };

  const handleTestReveal = (id) => {
    setTestReveal(prev => ({ ...prev, [id]: true }));
  };

  const scrollToMathSection = () => {
    mathSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Add real chart data for interpret questions
  const interpretBarData = {
    labels: ['Product A', 'Product B', 'Product C'],
    datasets: [{
      label: 'Sales',
      data: [120, 90, 150],
      backgroundColor: ['#3b82f6', '#f59e42', '#10b981'],
    }],
  };
  const interpretLineData = {
    labels: ['Q1', 'Q2', 'Q3', 'Q4'],
    datasets: [{
      label: 'Revenue',
      data: [200, 250, 300, 400],
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59,130,246,0.1)',
      tension: 0.3,
    }],
  };
  const interpretPieData = {
    labels: ['North', 'South', 'East', 'West'],
    datasets: [{
      data: [40, 30, 20, 10],
      backgroundColor: ['#3b82f6', '#f59e42', '#10b981', '#f43f5e'],
      borderWidth: 1,
    }],
  };
  const interpretScatterData = {
    datasets: [{
      label: 'Study Hours vs Score',
      data: [
        { x: 2, y: 60 },
        { x: 4, y: 70 },
        { x: 6, y: 80 },
        { x: 8, y: 90 },
      ],
      backgroundColor: '#3b82f6',
    }],
  };

  // Add chart options
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 10
        }
      }
    }
  };

  // Chart & Graph Test Questions
  const chartTestQuestions = [
    // 1. Interpret: Bar Chart
    {
      id: 1,
      type: 'interpret',
      question: 'Looking at this bar chart, what is the exact sales figure for Product B?',
      chart: <Bar data={interpretBarData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: '90 units', value: '90' },
        { label: '120 units', value: '120' },
        { label: '150 units', value: '150' },
        { label: 'Cannot determine', value: 'unknown' },
      ],
      correctAnswer: '90',
      explanation: 'Product B has exactly 90 units in sales according to the bar chart.'
    },
    // 2. Multiple choice: best for time series
    {
      id: 2,
      type: 'mc',
      question: 'Which chart type is best for showing trends over time?',
      options: [
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Bar Chart', value: 'bar', emoji: '📊' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'line',
      explanation: 'Line charts are ideal for showing trends over time.'
    },
    // 3. Interpret: Line Chart
    {
      id: 3,
      type: 'interpret',
      question: 'What is the trend shown in this line chart?',
      chart: <Line data={interpretLineData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: 'Steady increase', value: 'increase' },
        { label: 'Steady decrease', value: 'decrease' },
        { label: 'Fluctuating', value: 'fluctuating' },
        { label: 'No trend', value: 'none' },
      ],
      correctAnswer: 'increase',
      explanation: 'The line shows a steady increase in revenue across quarters.'
    },
    // 4. Multiple choice: best for proportions
    {
      id: 4,
      type: 'mc',
      question: 'Which chart is best for showing proportions of a whole?',
      options: [
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Bar Chart', value: 'bar', emoji: '📊' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'pie',
      explanation: 'Pie charts are best for showing proportions of a whole.'
    },
    // 5. Interpret: Pie Chart
    {
      id: 5,
      type: 'interpret',
      question: 'What is the combined market share of North and South regions according to this pie chart?',
      chart: <Pie data={interpretPieData} options={pieChartOptions} />,
      options: [
        { label: '50%', value: '50' },
        { label: '60%', value: '60' },
        { label: '70%', value: '70' },
        { label: '80%', value: '80' },
      ],
      correctAnswer: '70',
      explanation: 'North (40%) + South (30%) = 70% combined market share.'
    },
    // 6. Multiple choice: best for correlations
    {
      id: 6,
      type: 'mc',
      question: 'Which chart type is best for showing correlation between two variables?',
      options: [
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
        { label: 'Bar Chart', value: 'bar', emoji: '📊' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
      ],
      correctAnswer: 'scatter',
      explanation: 'Scatter plots are best for showing correlation between two variables.'
    },
    // 7. Interpret: Scatter Plot
    {
      id: 7,
      type: 'interpret',
      question: 'What type of correlation is shown in this scatter plot?',
      chart: <Scatter data={interpretScatterData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: 'Positive correlation', value: 'positive' },
        { label: 'Negative correlation', value: 'negative' },
        { label: 'No correlation', value: 'none' },
        { label: 'Perfect correlation', value: 'perfect' },
      ],
      correctAnswer: 'positive',
      explanation: 'As study hours increase, test scores also increase, showing positive correlation.'
    },
    // 8. Multiple choice: best for comparing categories
    {
      id: 8,
      type: 'mc',
      question: 'Which chart is best for comparing quantities across different categories?',
      options: [
        { label: 'Bar Chart', value: 'bar', emoji: '📊' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'bar',
      explanation: 'Bar charts are best for comparing quantities across categories.'
    },
    // 9. Interpret: Bar Chart (comparison)
    {
      id: 9,
      type: 'interpret',
      question: 'What is the difference in sales between Product A and Product B?',
      chart: <Bar data={interpretBarData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: '20 units', value: '20' },
        { label: '30 units', value: '30' },
        { label: '40 units', value: '40' },
        { label: '50 units', value: '50' },
      ],
      correctAnswer: '30',
      explanation: 'Product A has 120 units and Product B has 90 units, difference is 30 units.'
    },
    // 10. Multiple choice: best for composition over time
    {
      id: 10,
      type: 'mc',
      question: 'Which chart type is best for showing how composition changes over time?',
      options: [
        { label: 'Stacked Bar Chart', value: 'stackedbar', emoji: '🟦🟧🟩' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'stackedbar',
      explanation: 'Stacked bar charts show how composition changes over time.'
    },
    // 11. Interpret: Line Chart (rate of change)
    {
      id: 11,
      type: 'interpret',
      question: 'Between which quarters was the largest increase in revenue?',
      chart: <Line data={interpretLineData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: 'Q1 to Q2', value: 'q1q2' },
        { label: 'Q2 to Q3', value: 'q2q3' },
        { label: 'Q3 to Q4', value: 'q3q4' },
        { label: 'All equal', value: 'equal' },
      ],
      correctAnswer: 'q3q4',
      explanation: 'The largest increase was from Q3 (300) to Q4 (400), a 100 unit increase.'
    },
    // 12. Multiple choice: best for frequency distribution
    {
      id: 12,
      type: 'mc',
      question: 'Which chart is best for showing frequency distribution?',
      options: [
        { label: 'Histogram', value: 'histogram', emoji: '📊' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'histogram',
      explanation: 'Histograms are best for showing frequency distributions.'
    },
    // 13. Interpret: Pie Chart (proportions)
    {
      id: 13,
      type: 'interpret',
      question: 'What percentage of the total does the South region represent?',
      chart: <Pie data={interpretPieData} options={pieChartOptions} />,
      options: [
        { label: '20%', value: '20' },
        { label: '30%', value: '30' },
        { label: '40%', value: '40' },
        { label: '10%', value: '10' },
      ],
      correctAnswer: '30',
      explanation: 'South region represents 30% of the total market share.'
    },
    // 14. Multiple choice: best for showing outliers
    {
      id: 14,
      type: 'mc',
      question: 'Which chart type is best for identifying outliers in data?',
      options: [
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
        { label: 'Bar Chart', value: 'bar', emoji: '📊' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
      ],
      correctAnswer: 'scatter',
      explanation: 'Scatter plots are best for identifying outliers in data.'
    },
    // 15. Interpret: Scatter Plot (prediction)
    {
      id: 15,
      type: 'interpret',
      question: 'Based on the trend, what score would you predict for 10 hours of study?',
      chart: <Scatter data={interpretScatterData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: '85', value: '85' },
        { label: '90', value: '90' },
        { label: '95', value: '95' },
        { label: '100', value: '100' },
      ],
      correctAnswer: '95',
      explanation: 'Based on the trend, 10 hours of study would likely result in a score around 95.'
    },
    // 16. Multiple choice: best for showing cumulative data
    {
      id: 16,
      type: 'mc',
      question: 'Which chart type is best for showing cumulative data over time?',
      options: [
        { label: 'Area Chart', value: 'area', emoji: '📈' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Bar Chart', value: 'bar', emoji: '📊' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'area',
      explanation: 'Area charts are best for showing cumulative data over time.'
    },
    // 17. Interpret: Bar Chart (percentage)
    {
      id: 17,
      type: 'interpret',
      question: 'What percentage of total sales does Product A represent?',
      chart: <Bar data={interpretBarData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: '25%', value: '25' },
        { label: '33%', value: '33' },
        { label: '40%', value: '40' },
        { label: '50%', value: '50' },
      ],
      correctAnswer: '33',
      explanation: 'Product A has 120 units out of 360 total units, which is approximately 33%.'
    },
    // 18. Multiple choice: best for showing rankings
    {
      id: 18,
      type: 'mc',
      question: 'Which chart type is best for showing rankings?',
      options: [
        { label: 'Horizontal Bar Chart', value: 'hbar', emoji: '📊' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'hbar',
      explanation: 'Horizontal bar charts are best for showing rankings.'
    },
    // 19. Interpret: Line Chart (seasonality)
    {
      id: 19,
      type: 'interpret',
      question: 'Is there any evidence of seasonality in this revenue data?',
      chart: <Line data={interpretLineData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: 'Yes, clear seasonal pattern', value: 'yes' },
        { label: 'No, steady increase', value: 'no' },
        { label: 'Yes, quarterly peaks', value: 'quarterly' },
        { label: 'Cannot determine', value: 'unknown' },
      ],
      correctAnswer: 'no',
      explanation: 'The data shows a steady increase without clear seasonal patterns.'
    },
    // 20. Multiple choice: best for showing relationships
    {
      id: 20,
      type: 'mc',
      question: 'Which chart type is best for showing relationships between multiple variables?',
      options: [
        { label: 'Bubble Chart', value: 'bubble', emoji: '🫧' },
        { label: 'Bar Chart', value: 'bar', emoji: '📊' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
      ],
      correctAnswer: 'bubble',
      explanation: 'Bubble charts are best for showing relationships between multiple variables.'
    },
    // 21. Interpret: Scatter Plot (new)
    {
      id: 21,
      type: 'interpret',
      question: 'Based on the scatter plot, what would be the predicted test score for a student who studies for 5 hours?',
      chart: <Scatter data={interpretScatterData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: '75', value: '75' },
        { label: '80', value: '80' },
        { label: '85', value: '85' },
        { label: '90', value: '90' },
      ],
      correctAnswer: '80',
      explanation: 'Looking at the trend line, a student who studies for 5 hours would likely score around 80 points.'
    },
    // 22. Interpret: Line Chart (specific data point)
    {
      id: 22,
      type: 'interpret',
      question: 'What was the exact revenue in Q2 according to this line chart?',
      chart: <Line data={interpretLineData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: '$200', value: '200' },
        { label: '$250', value: '250' },
        { label: '$300', value: '300' },
        { label: '$400', value: '400' },
      ],
      correctAnswer: '250',
      explanation: 'The line chart shows Q2 revenue was exactly $250.'
    },
    // Add new stacked bar chart questions
    {
      id: 21,
      type: 'interpret',
      question: 'Looking at this stacked bar chart, what is the total sales for Q3 across all products?',
      chart: <Bar 
        data={stackedBarTestData} 
        options={{
          scales: {
            x: { stacked: true },
            y: { stacked: true }
          }
        }} 
      />,
      options: [
        { label: '90 units', value: '90' },
        { label: '110 units', value: '110' },
        { label: '120 units', value: '120' },
        { label: '130 units', value: '130' },
      ],
      correctAnswer: '110',
      explanation: 'Q3 total is the sum of all products: Product A (45) + Product B (35) + Product C (30) = 110 units.'
    },
    {
      id: 22,
      type: 'interpret',
      question: 'In this stacked bar chart, which product showed the highest growth from Q1 to Q4?',
      chart: <Bar 
        data={stackedBarTestData} 
        options={{
          scales: {
            x: { stacked: true },
            y: { stacked: true }
          }
        }} 
      />,
      options: [
        { label: 'Product A', value: 'A' },
        { label: 'Product B', value: 'B' },
        { label: 'Product C', value: 'C' },
        { label: 'Cannot determine', value: 'unknown' },
      ],
      correctAnswer: 'C',
      explanation: 'Product C grew from 20 to 35 units (75% increase), while Product A grew from 40 to 50 (25% increase) and Product B from 30 to 45 (50% increase).'
    },
    {
      id: 23,
      type: 'interpret',
      question: 'What is the histogram showing?',
      chart: <Bar 
        data={histogramTestData}
        options={{
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Number of Students'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Test Score Range'
              }
            }
          }
        }}
      />,
      options: [
        { label: 'Distribution of test scores', value: 'distribution' },
        { label: 'Average test scores over time', value: 'average' },
        { label: 'Comparison of different tests', value: 'comparison' },
        { label: 'Individual student performance', value: 'individual' },
      ],
      correctAnswer: 'distribution',
      explanation: 'A histogram shows the distribution of data across different ranges or bins. In this case, it shows how many students scored in each range.'
    },
    {
      id: 24,
      type: 'interpret',
      question: 'Based on the histogram, what is the most common score range?',
      chart: <Bar 
        data={histogramTestData}
        options={{
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Number of Students'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Test Score Range'
              }
            }
          }
        }}
      />,
      options: [
        { label: '41-50', value: '41-50' },
        { label: '51-60', value: '51-60' },
        { label: '61-70', value: '61-70' },
        { label: '71-80', value: '71-80' },
      ],
      correctAnswer: '41-50',
      explanation: 'The highest bar in the histogram is in the 41-50 range, with 20 students scoring in this range.'
    },
    {
      id: 25,
      type: 'interpret',
      question: 'In the stacked bar chart, what percentage of total Q2 sales does Product B represent?',
      chart: <Bar 
        data={stackedBarTestData} 
        options={{
          scales: {
            x: { stacked: true },
            y: { stacked: true }
          }
        }} 
      />,
      options: [
        { label: '30%', value: '30' },
        { label: '35%', value: '35' },
        { label: '40%', value: '40' },
        { label: '45%', value: '45' },
      ],
      correctAnswer: '40',
      explanation: 'Q2 total sales are 100 units (35 + 40 + 25). Product B has 40 units, which is 40% of the total.'
    }
  ];

  // Add Chart Test 2 questions
  const chartTest2Questions = [
    // 1. Interpret: Bar Chart (new data)
    {
      id: 201,
      type: 'interpret',
      question: 'Looking at this bar chart, what is the difference in sales between Product A and Product C?',
      chart: <Bar data={interpretBarData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: '20 units', value: '20' },
        { label: '30 units', value: '30' },
        { label: '40 units', value: '40' },
        { label: '50 units', value: '50' },
      ],
      correctAnswer: '30',
      explanation: 'Product A has 120 units and Product C has 150 units, difference is 30 units.'
    },
    // 2. Multiple choice: best for showing trends
    {
      id: 202,
      type: 'mc',
      question: 'Which chart type is best for showing seasonal patterns?',
      options: [
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Bar Chart', value: 'bar', emoji: '📊' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'line',
      explanation: 'Line charts are best for showing seasonal patterns over time.'
    },
    // 3. Interpret: Line Chart (new data)
    {
      id: 203,
      type: 'interpret',
      question: 'What is the rate of change in revenue between Q2 and Q3?',
      chart: <Line data={interpretLineData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: '$25 per quarter', value: '25' },
        { label: '$50 per quarter', value: '50' },
        { label: '$75 per quarter', value: '75' },
        { label: '$100 per quarter', value: '100' },
      ],
      correctAnswer: '50',
      explanation: 'Revenue increased from $250 to $300, a change of $50 per quarter.'
    },
    // 4. Multiple choice: best for showing distributions
    {
      id: 204,
      type: 'mc',
      question: 'Which chart type is best for showing the distribution of a dataset?',
      options: [
        { label: 'Histogram', value: 'histogram', emoji: '📊' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'histogram',
      explanation: 'Histograms are best for showing the distribution of data.'
    },
    // 5. Interpret: Pie Chart (new data)
    {
      id: 205,
      type: 'interpret',
      question: 'What is the combined market share of East and West regions?',
      chart: <Pie data={interpretPieData} options={pieChartOptions} />,
      options: [
        { label: '20%', value: '20' },
        { label: '30%', value: '30' },
        { label: '40%', value: '40' },
        { label: '50%', value: '50' },
      ],
      correctAnswer: '30',
      explanation: 'East (20%) + West (10%) = 30% combined market share.'
    },
    // 6. Multiple choice: best for showing correlations
    {
      id: 206,
      type: 'mc',
      question: 'Which chart type is best for showing the relationship between two continuous variables?',
      options: [
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
        { label: 'Bar Chart', value: 'bar', emoji: '📊' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
      ],
      correctAnswer: 'scatter',
      explanation: 'Scatter plots are best for showing relationships between two continuous variables.'
    },
    // 7. Interpret: Scatter Plot (new data)
    {
      id: 207,
      type: 'interpret',
      question: 'What type of correlation is shown in this scatter plot?',
      chart: <Scatter data={interpretScatterData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: 'Strong positive correlation', value: 'strong_positive' },
        { label: 'Weak positive correlation', value: 'weak_positive' },
        { label: 'No correlation', value: 'none' },
        { label: 'Negative correlation', value: 'negative' },
      ],
      correctAnswer: 'strong_positive',
      explanation: 'The dots show a clear upward trend, indicating a strong positive correlation.'
    },
    // 8. Multiple choice: best for showing rankings
    {
      id: 208,
      type: 'mc',
      question: 'Which chart type is best for showing rankings of multiple items?',
      options: [
        { label: 'Horizontal Bar Chart', value: 'hbar', emoji: '📊' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'hbar',
      explanation: 'Horizontal bar charts are best for showing rankings.'
    },
    // 9. Interpret: Bar Chart (comparison)
    {
      id: 209,
      type: 'interpret',
      question: 'What percentage of total sales does Product B represent?',
      chart: <Bar data={interpretBarData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: '20%', value: '20' },
        { label: '25%', value: '25' },
        { label: '30%', value: '30' },
        { label: '35%', value: '35' },
      ],
      correctAnswer: '25',
      explanation: 'Product B has 90 units out of 360 total units, which is 25%.'
    },
    // 10. Multiple choice: best for showing composition
    {
      id: 210,
      type: 'mc',
      question: 'Which chart type is best for showing how a whole is divided into parts?',
      options: [
        { label: 'Stacked Bar Chart', value: 'stackedbar', emoji: '🟦🟧🟩' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'stackedbar',
      explanation: 'Stacked bar charts are best for showing how a whole is divided into parts.'
    },
    // 11. Interpret: Line Chart (trend)
    {
      id: 211,
      type: 'interpret',
      question: 'What is the overall trend in this revenue data?',
      chart: <Line data={interpretLineData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: 'Steady increase', value: 'steady_increase' },
        { label: 'Fluctuating', value: 'fluctuating' },
        { label: 'Steady decrease', value: 'steady_decrease' },
        { label: 'No clear trend', value: 'no_trend' },
      ],
      correctAnswer: 'steady_increase',
      explanation: 'The line shows a consistent upward trend across all quarters.'
    },
    // 12. Multiple choice: best for showing outliers
    {
      id: 212,
      type: 'mc',
      question: 'Which chart type is best for identifying outliers in a dataset?',
      options: [
        { label: 'Box Plot', value: 'boxplot', emoji: '📦' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Bar Chart', value: 'bar', emoji: '📊' },
      ],
      correctAnswer: 'boxplot',
      explanation: 'Box plots are best for identifying outliers in a dataset.'
    },
    // 13. Interpret: Pie Chart (proportions)
    {
      id: 213,
      type: 'interpret',
      question: 'What is the ratio of North region to South region market share?',
      chart: <Pie data={interpretPieData} options={pieChartOptions} />,
      options: [
        { label: '2:1', value: '2:1' },
        { label: '3:2', value: '3:2' },
        { label: '4:3', value: '4:3' },
        { label: '5:4', value: '5:4' },
      ],
      correctAnswer: '4:3',
      explanation: 'North (40%) to South (30%) ratio is 4:3.'
    },
    // 14. Multiple choice: best for showing cumulative data
    {
      id: 214,
      type: 'mc',
      question: 'Which chart type is best for showing cumulative totals over time?',
      options: [
        { label: 'Area Chart', value: 'area', emoji: '📈' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Bar Chart', value: 'bar', emoji: '📊' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'area',
      explanation: 'Area charts are best for showing cumulative totals over time.'
    },
    // 15. Interpret: Scatter Plot (prediction)
    {
      id: 215,
      type: 'interpret',
      question: 'Based on the trend, what score would you predict for 12 hours of study?',
      chart: <Scatter data={interpretScatterData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: '95', value: '95' },
        { label: '98', value: '98' },
        { label: '100', value: '100' },
        { label: 'Cannot predict', value: 'unknown' },
      ],
      correctAnswer: '98',
      explanation: 'Based on the trend line, 12 hours of study would likely result in a score around 98.'
    },
    // 16. Multiple choice: best for showing multiple variables
    {
      id: 216,
      type: 'mc',
      question: 'Which chart type is best for showing relationships between three or more variables?',
      options: [
        { label: 'Bubble Chart', value: 'bubble', emoji: '🫧' },
        { label: 'Bar Chart', value: 'bar', emoji: '📊' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
      ],
      correctAnswer: 'bubble',
      explanation: 'Bubble charts are best for showing relationships between three or more variables.'
    },
    // 17. Interpret: Bar Chart (percentage)
    {
      id: 217,
      type: 'interpret',
      question: 'What is the percentage increase in sales from Product A to Product C?',
      chart: <Bar data={interpretBarData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: '20%', value: '20' },
        { label: '25%', value: '25' },
        { label: '30%', value: '30' },
        { label: '35%', value: '35' },
      ],
      correctAnswer: '25',
      explanation: 'Product A has 120 units and Product C has 150 units, a 25% increase.'
    },
    // 18. Multiple choice: best for showing proportions
    {
      id: 218,
      type: 'mc',
      question: 'Which chart type is best for showing proportions of a whole when you have more than 5 categories?',
      options: [
        { label: 'Stacked Bar Chart', value: 'stackedbar', emoji: '🟦🟧🟩' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'stackedbar',
      explanation: 'Stacked bar charts are better than pie charts for showing proportions with many categories.'
    },
    // 19. Interpret: Line Chart (specific point)
    {
      id: 219,
      type: 'interpret',
      question: 'What was the exact revenue in Q3 according to this line chart?',
      chart: <Line data={interpretLineData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: '$250', value: '250' },
        { label: '$300', value: '300' },
        { label: '$350', value: '350' },
        { label: '$400', value: '400' },
      ],
      correctAnswer: '300',
      explanation: 'The line chart shows Q3 revenue was exactly $300.'
    },
    // 20. Multiple choice: best for showing changes
    {
      id: 220,
      type: 'mc',
      question: 'Which chart type is best for showing changes in values over time?',
      options: [
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Bar Chart', value: 'bar', emoji: '📊' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'line',
      explanation: 'Line charts are best for showing changes in values over time.'
    },
    // 21. Interpret: Scatter Plot (correlation)
    {
      id: 221,
      type: 'interpret',
      question: 'What is the approximate correlation coefficient for this scatter plot?',
      chart: <Scatter data={interpretScatterData} options={{ plugins: { legend: { display: false } } }} />,
      options: [
        { label: '0.5', value: '0.5' },
        { label: '0.7', value: '0.7' },
        { label: '0.9', value: '0.9' },
        { label: '1.0', value: '1.0' },
      ],
      correctAnswer: '0.9',
      explanation: 'The strong positive correlation suggests a correlation coefficient close to 0.9.'
    },
    // 22. Multiple choice: best for showing distributions
    {
      id: 222,
      type: 'mc',
      question: 'Which chart type is best for showing the distribution of a continuous variable?',
      options: [
        { label: 'Histogram', value: 'histogram', emoji: '📊' },
        { label: 'Pie Chart', value: 'pie', emoji: '🥧' },
        { label: 'Line Chart', value: 'line', emoji: '📈' },
        { label: 'Scatter Plot', value: 'scatter', emoji: '🔵' },
      ],
      correctAnswer: 'histogram',
      explanation: 'Histograms are best for showing the distribution of continuous variables.'
    },
    // Add new stacked bar chart questions (more complex)
    {
      id: 223,  // Changed from 16
      type: 'interpret',
      question: 'In this stacked bar chart, if Product A sales increase by 20% in Q5, and Products B and C maintain their Q4 levels, what would be the new total sales?',
      chart: <Bar 
        data={stackedBarTestData} 
        options={{
          scales: {
            x: { stacked: true },
            y: { stacked: true }
          }
        }} 
      />,
      options: [
        { label: '130 units', value: '130' },
        { label: '140 units', value: '140' },
        { label: '150 units', value: '150' },
        { label: '160 units', value: '160' },
      ],
      correctAnswer: '140',  // Fixed the answer to match the explanation
      explanation: 'Product A Q4 (50) + 20% = 60, Product B Q4 (45), Product C Q4 (35). Total = 60 + 45 + 35 = 140 units.'
    },
    {
      id: 224,  // Changed from 17
      type: 'interpret',
      question: 'Based on the histogram, what percentage of students scored above 70?',
      chart: <Bar 
        data={histogramTestData}
        options={{
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Number of Students'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Test Score Range'
              }
            }
          }
        }}
      />,
      options: [
        { label: '10%', value: '10' },
        { label: '15%', value: '15' },
        { label: '20%', value: '20' },
        { label: '25%', value: '25' },
      ],
      correctAnswer: '10',  // Fixed the answer to match the explanation
      explanation: 'Total students = 100. Students above 70 = 6 + 3 + 1 = 10. Percentage = (10/100) × 100 = 10%.'
    },
    {
      id: 225,  // Changed from 18
      type: 'interpret',
      question: 'In the stacked bar chart, if the total sales target for Q3 was 120 units, by what percentage did the actual sales fall short?',
      chart: <Bar 
        data={stackedBarTestData} 
        options={{
          scales: {
            x: { stacked: true },
            y: { stacked: true }
          }
        }} 
      />,
      options: [
        { label: '5%', value: '5' },
        { label: '8.3%', value: '8.3' },
        { label: '10%', value: '10' },
        { label: '12.5%', value: '12.5' },
      ],
      correctAnswer: '8.3',
      explanation: 'Actual Q3 sales = 110 units. Target = 120 units. Shortfall = (120 - 110)/120 × 100 = 8.3%.'
    },
    {
      id: 226,  // Changed from 19
      type: 'interpret',
      question: 'Based on the histogram, what is the median score range?',
      chart: <Bar 
        data={histogramTestData}
        options={{
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Number of Students'
              }
            },
            x: {
              title: {
                display: true,
                text: 'Test Score Range'
              }
            }
          }
        }}
      />,
      options: [
        { label: '41-50', value: '41-50' },
        { label: '51-60', value: '51-60' },
        { label: '61-70', value: '61-70' },
        { label: 'Cannot determine', value: 'unknown' },
      ],
      correctAnswer: '41-50',
      explanation: 'With 100 total students, the median would be between the 50th and 51st student. Counting up from the lowest range, we reach the 50th student in the 41-50 range.'
    },
    {
      id: 227,  // Changed from 20
      type: 'interpret',
      question: 'In the stacked bar chart, if Product C sales in Q4 represent 25% of its annual sales, what were its total annual sales?',
      chart: <Bar 
        data={stackedBarTestData} 
        options={{
          scales: {
            x: { stacked: true },
            y: { stacked: true }
          }
        }} 
      />,
      options: [
        { label: '110 units', value: '110' },
        { label: '120 units', value: '120' },
        { label: '130 units', value: '130' },
        { label: '140 units', value: '140' },
      ],
      correctAnswer: '140',
      explanation: 'Product C Q4 sales = 35 units. If this is 25% of annual sales, then annual sales = 35 × 4 = 140 units.'
    }
  ];

  // Add test switching handlers
  const handleMathTestSwitch = () => {
    setActiveMathTest(prev => prev === 1 ? 2 : 1);
  };

  const handleChartTestSwitch = () => {
    setActiveChartTest(prev => prev === 1 ? 2 : 1);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Consulting Math Guides",
    "description": "Master consulting math with our comprehensive guides. From mental math techniques to case interview calculations, learn essential skills for consulting success with interactive practice problems and expert strategies.",
    "about": {
      "@type": "Thing",
      "name": "Consulting Mathematics",
      "description": "Essential mathematical skills for consulting professionals"
    },
    "hasPart": [
      {
        "@type": "Article",
        "name": "Mental Math Techniques",
        "description": "Learn rapid calculation methods for consulting interviews"
      },
      {
        "@type": "Article",
        "name": "Case Math Fundamentals",
        "description": "Master the mathematical foundations of case interviews"
      },
      {
        "@type": "Article",
        "name": "Advanced Calculations",
        "description": "Advanced mathematical techniques for complex case scenarios"
      }
    ],
    "educationalLevel": "Advanced",
    "audience": {
      "@type": "Audience",
      "audienceType": "Consulting Professionals"
    }
  };

  return (
    <>
      <SEO
        title="Consulting Math Guides | Master Case Interview Calculations"
        description="Master consulting math with our comprehensive guides. From mental math techniques to case interview calculations, learn essential skills for consulting success with interactive practice problems and expert strategies."
        keywords={[
          'consulting math',
          'case interview math',
          'mental math techniques',
          'consulting calculations',
          'case math practice',
          'consulting interview prep',
          'business math skills'
        ]}
        url="/consulting-math-guides"
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-gray-50">
        {/* Side Navigation */}
        <div className="fixed left-6 top-[285px] flex flex-col gap-6 z-[9999] hidden md:flex">
          <div className="group relative">
            <a 
              href="#math" 
              className="flex items-center"
              aria-label="Math Guide Section"
            >
              <div className="absolute left-full ml-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Math Guide
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <span className="text-xl">🧮</span>
              </div>
            </a>
          </div>

          <div className="group relative">
            <a 
              href="#math-test" 
              className="flex items-center"
              aria-label="Math Test Section"
            >
              <div className="absolute left-full ml-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Math Test
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <span className="text-xl">📝</span>
              </div>
            </a>
          </div>

          <div className="group relative">
            <a 
              href="#charts" 
              className="flex items-center"
              aria-label="Chart Types Section"
            >
              <div className="absolute left-full ml-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Chart Types
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <span className="text-xl">📊</span>
              </div>
            </a>
          </div>

          <div className="group relative">
            <a 
              href="#chart-test" 
              className="flex items-center"
              aria-label="Chart Test Section"
            >
              <div className="absolute left-full ml-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Chart Test
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <span className="text-xl">✅</span>
              </div>
            </a>
          </div>
        </div>
        {/* Mobile Bottom Nav */}
        <div className="fixed bottom-0 left-0 w-full flex justify-around bg-white/90 shadow-lg py-2 z-[9999] md:hidden">
          <a href="#math" aria-label="Math Guide Section" className="flex flex-col items-center text-xs text-blue-700"><span className="text-xl">🧮</span>Math</a>
          <a href="#math-test" aria-label="Math Test Section" className="flex flex-col items-center text-xs text-blue-700"><span className="text-xl">📝</span>Test</a>
          <a href="#charts" aria-label="Chart Types Section" className="flex flex-col items-center text-xs text-blue-700"><span className="text-xl">📊</span>Charts</a>
          <a href="#chart-test" aria-label="Chart Test Section" className="flex flex-col items-center text-xs text-blue-700"><span className="text-xl">✅</span>Quiz</a>
        </div>

        {/* Hero Section */}
        <section className="relative flex flex-col items-center justify-center min-h-[350px] md:min-h-[600px] py-10 md:py-20 px-3 sm:px-2 md:px-6">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-gray-800/85 to-gray-700/80">
            <div className="absolute inset-0" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              mixBlendMode: 'overlay'
            }} />
          </div>
          <div className="container pl-0 md:pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 py-8 md:py-24 relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl hero-content"
            >
              <h1 className="text-2xl md:text-4xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight text-white">
                Free Guides and Tests to Master Consulting Math & Charts
              </h1>
              <p className="text-base md:text-xl lg:text-2xl text-white/90 mb-6 md:mb-10 leading-relaxed">
                Learn essential math skills and chart interpretation techniques for consulting success.
              </p>
              <div className="flex gap-4 md:gap-6 mt-4 md:mt-8">
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={scrollToMathSection}
                  className="group relative px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-base md:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
                >
                  Start Learning
                  <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/redrock')}
                  className="group relative px-6 md:px-8 py-3 md:py-4 border-2 border-blue-600 text-blue-600 text-base md:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:bg-blue-50 bg-white/80 backdrop-blur-sm"
                >
                  Feeling Confident? Try RedRockCases
                  <div className="absolute inset-0 bg-blue-600 rounded-xl opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                </motion.button>
              </div>
            </motion.div>
            {/* Consultants image only on desktop */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden md:block absolute top-[120px] right-10 md:right-20 w-[400px] aspect-[2/3] z-10 hero-image"
            >
              <div className="h-full rounded-xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                <img 
                  src={consultantsImage} 
                  alt="Consultants" 
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-20 bg-gradient-to-b from-white via-blue-50/30 to-white text-center px-3 sm:px-2 md:px-6 relative">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold section-title tracking-tight mb-8 md:mb-12">Pro Tips for Success</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            <div className="feature-card p-6">
              <div className="text-3xl mb-4">🧠</div>
              <h3 className="text-lg font-semibold mb-2">Cognitive Strategy</h3>
              <p className="text-gray-600">Avoid calculator overuse, use scratchpad instead</p>
            </div>
            
            <div className="feature-card p-6">
              <div className="text-3xl mb-4">📊</div>
              <h3 className="text-lg font-semibold mb-2">Graph Interpretation</h3>
              <p className="text-gray-600">Choose based on comparison type: trend vs category vs composition</p>
            </div>
            
            <div className="feature-card p-6">
              <div className="text-3xl mb-4">⏱</div>
              <h3 className="text-lg font-semibold mb-2">Time Management</h3>
              <p className="text-gray-600">Spend 10–12 mins max on Investigation, leave 5–8 mins for Report</p>
            </div>
          </div>
        </section>

        {/* Math Guide Section */}
        <section id="math" ref={mathSectionRef} className="py-16 md:py-24 bg-gradient-to-b from-blue-50 via-white to-blue-50 text-center px-3 sm:px-2 md:px-6 relative">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold section-title tracking-tight mb-8 md:mb-12">Essential Math Skills</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-6xl mx-auto">
            {/* Percentage Calculations */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="math-card group hover:scale-[1.03] bg-gradient-to-br from-blue-100 via-white to-blue-200 border-0 shadow-xl rounded-3xl p-10 flex flex-col gap-8 relative overflow-hidden"
            >
              <div className="absolute -top-6 -right-6 bg-gradient-to-br from-blue-400 to-blue-600 w-20 h-20 rounded-full opacity-20 z-0"></div>
              <div className="flex items-center gap-4 mb-4 z-10">
                <span className="text-4xl">📊</span>
                <h3 className="text-2xl font-bold text-blue-700">Percentage Calculations</h3>
              </div>
              <div className="space-y-8 z-10">
                <div className="bg-white/80 p-6 rounded-2xl border-l-4 border-blue-400 shadow-sm text-left">
                  <h4 className="font-semibold text-blue-600 mb-2 text-lg">Finding a Percentage of a Number</h4>
                  <p className="text-gray-700 mb-2 leading-relaxed">
                    <span className="font-semibold">Formula:</span> (Percentage ÷ 100) × Total Value
                  </p>
                  <div className="bg-blue-50 p-4 rounded-xl">
                    <p className="text-gray-700 mb-1">
                      <span className="font-semibold">Example:</span> What is 20% of 150?
                    </p>
                    <p className="text-blue-700 font-medium">Calculation: (20 ÷ 100) × 150 = 0.2 × 150 = 30</p>
                  </div>
                </div>
                <div className="bg-white/80 p-6 rounded-2xl border-l-4 border-blue-400 shadow-sm text-left">
                  <h4 className="font-semibold text-blue-600 mb-2 text-lg">Calculating Percentage Change</h4>
                  <p className="text-gray-700 mb-2 leading-relaxed">
                    <span className="font-semibold">Formula:</span> [(New Value - Original Value) ÷ Original Value] × 100
                  </p>
                  <div className="bg-blue-50 p-4 rounded-xl">
                    <p className="text-gray-700 mb-1">
                      <span className="font-semibold">Example:</span> If a price increases from $50 to $65:
                    </p>
                    <p className="text-blue-700 font-medium">Calculation: [(65 - 50) ÷ 50] × 100 = 30% increase</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Fraction Calculations */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="math-card group hover:scale-[1.03] bg-gradient-to-br from-purple-100 via-white to-purple-200 border-0 shadow-xl rounded-3xl p-10 flex flex-col gap-8 relative overflow-hidden"
            >
              <div className="absolute -top-6 -right-6 bg-gradient-to-br from-purple-400 to-purple-600 w-20 h-20 rounded-full opacity-20 z-0"></div>
              <div className="flex items-center gap-4 mb-4 z-10">
                <span className="text-4xl">🔢</span>
                <h3 className="text-2xl font-bold text-purple-700">Fraction Calculations</h3>
              </div>
              <div className="space-y-8 z-10">
                <div className="bg-white/80 p-6 rounded-2xl border-l-4 border-purple-400 shadow-sm text-left">
                  <h4 className="font-semibold text-purple-600 mb-2 text-lg">Adding/Subtracting Fractions</h4>
                  <p className="text-gray-700 mb-2 leading-relaxed">
                    <span className="font-semibold">Steps:</span>
                    <ul className="list-disc list-inside mt-1 text-gray-600">
                      <li>Ensure denominators are the same</li>
                      <li>Add or subtract numerators</li>
                    </ul>
                  </p>
                  <div className="bg-purple-50 p-4 rounded-xl">
                    <p className="text-gray-700 mb-1">
                      <span className="font-semibold">Example:</span> 1/4 + 3/4 = (1+3)/4 = 4/4 = 1
                    </p>
                  </div>
                </div>
                <div className="bg-white/80 p-6 rounded-2xl border-l-4 border-purple-400 shadow-sm text-left">
                  <h4 className="font-semibold text-purple-600 mb-2 text-lg">Multiplying/Dividing Fractions</h4>
                  <p className="text-gray-700 mb-2 leading-relaxed">
                    <span className="font-semibold">Multiplying:</span> Multiply numerators and denominators
                  </p>
                  <div className="bg-purple-50 p-4 rounded-xl mb-2">
                    <p className="text-gray-700 mb-1">
                      <span className="font-semibold">Example:</span> 2/3 × 3/5 = (2×3)/(3×5) = 6/15 = 2/5
                    </p>
                  </div>
                  <p className="text-gray-700 mt-3 leading-relaxed">
                    <span className="font-semibold">Dividing:</span> Multiply by reciprocal
                  </p>
                  <div className="bg-purple-50 p-4 rounded-xl">
                    <p className="text-gray-700 mb-1">
                      <span className="font-semibold">Example:</span> (2/3) ÷ (4/5) = (2/3) × (5/4) = 10/12 = 5/6
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Proportion Calculations */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="math-card group hover:scale-[1.03] bg-gradient-to-br from-green-100 via-white to-green-200 border-0 shadow-xl rounded-3xl p-10 flex flex-col gap-8 relative overflow-hidden"
            >
              <div className="absolute -top-6 -right-6 bg-gradient-to-br from-green-400 to-green-600 w-20 h-20 rounded-full opacity-20 z-0"></div>
              <div className="flex items-center gap-4 mb-4 z-10">
                <span className="text-4xl">⚖️</span>
                <h3 className="text-2xl font-bold text-green-700">Proportion Calculations</h3>
              </div>
              <div className="space-y-8 z-10">
                <div className="bg-white/80 p-6 rounded-2xl border-l-4 border-green-400 shadow-sm text-left">
                  <h4 className="font-semibold text-green-600 mb-2 text-lg">Understanding Proportions</h4>
                  <p className="text-gray-700 mb-2 leading-relaxed">
                    A proportion states that two ratios are equal.
                  </p>
                  <div className="bg-green-50 p-4 rounded-xl">
                    <p className="text-gray-700 mb-1">
                      <span className="font-semibold">Example:</span> If 2/3 = x/6, find x.
                    </p>
                    <p className="text-green-700 font-medium">Cross-multiply: 2 × 6 = 3 × x → 12 = 3x → x = 4</p>
                  </div>
                </div>
                <div className="bg-white/80 p-6 rounded-2xl border-l-4 border-green-400 shadow-sm text-left">
                  <h4 className="font-semibold text-green-600 mb-2 text-lg">Solving Proportions</h4>
                  <p className="text-gray-700 mb-2 leading-relaxed">
                    <span className="font-semibold">Steps:</span>
                    <ul className="list-disc list-inside mt-1 text-gray-600">
                      <li>Cross-multiply the known values</li>
                      <li>Divide to find the unknown variable</li>
                    </ul>
                  </p>
                  <div className="bg-green-50 p-4 rounded-xl">
                    <p className="text-gray-700 mb-1">
                      <span className="font-semibold">Example:</span> If 5/8 = 15/x, find x.
                    </p>
                    <p className="text-green-700 font-medium">Cross-multiply: 5 × x = 8 × 15 → 5x = 120 → x = 24</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Formula Bank */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="math-card group hover:scale-[1.03] bg-gradient-to-br from-yellow-100 via-white to-yellow-200 border-0 shadow-xl rounded-3xl p-10 flex flex-col gap-8 relative overflow-hidden"
            >
              <div className="absolute -top-6 -right-6 bg-gradient-to-br from-yellow-400 to-yellow-600 w-20 h-20 rounded-full opacity-20 z-0"></div>
              <div className="flex items-center gap-4 mb-4 z-10">
                <span className="text-4xl">📚</span>
                <h3 className="text-2xl font-bold text-yellow-700">Formula Bank</h3>
              </div>
              <div className="bg-white/80 p-6 rounded-2xl border-l-4 border-yellow-400 shadow-sm text-left z-10">
                <h4 className="font-semibold text-yellow-600 mb-3 text-lg">Common Formulas</h4>
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-yellow-50 p-4 rounded-xl">
                    <p className="text-gray-700">
                      <span className="font-semibold">% Change:</span> (New - Old) / Old
                    </p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-xl">
                    <p className="text-gray-700">
                      <span className="font-semibold">% Point Difference:</span> New % - Old %
                    </p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-xl">
                    <p className="text-gray-700">
                      <span className="font-semibold">Average of Two:</span> (Value A + Value B)/2
                    </p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-xl">
                    <p className="text-gray-700">
                      <span className="font-semibold">Weighted Average:</span> Σ(Value × Weight) / Σ(Weights)
                    </p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-xl">
                    <p className="text-gray-700">
                      <span className="font-semibold">Probability (At Least One):</span> 1 - P(none)
                    </p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-xl">
                    <p className="text-gray-700">
                      <span className="font-semibold">Expected Value:</span> Σ(Probability × Value)
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Add Practice Button after Math Section */}
          <div className="mt-16 text-center">
            <p className="text-xl text-gray-600 mb-6">Ready to put your math skills to the test?</p>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/redrock')}
              className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
            >
              Practice in RedRockCases
              <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
            </motion.button>
          </div>
        </section>

        {/* Test Your Learning Section */}
        <div id="math-test" className="mt-20">
          <div className="flex items-center justify-center gap-4 mb-12">
            <h2 className="text-4xl font-bold section-title tracking-tight">Test Your Learning</h2>
            <div className="relative">
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <div className="h-0.5 w-8 bg-gradient-to-r from-transparent to-blue-500"></div>
                  <span className="text-blue-500 font-medium tracking-wider animate-pulse">PRESS ME</span>
                  <div className="h-0.5 w-8 bg-gradient-to-l from-transparent to-blue-500"></div>
                </div>
              </div>
              <motion.button 
                whileHover={{ scale: 1.1, rotate: 180 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsTestSectionOpen(!isTestSectionOpen)}
                className="w-11 h-11 rounded-full bg-gradient-to-r from-blue-600 to-blue-400 flex items-center justify-center text-white shadow-lg transform transition-transform duration-200 group"
              >
                <span className="text-xl transform group-hover:rotate-12 transition-transform duration-200">🔌</span>
              </motion.button>
            </div>
          </div>
          
          <AnimatePresence>
            {isTestSectionOpen && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="overflow-hidden relative"
              >
                {/* Learning Note */}
                <div className="max-w-3xl mx-auto mb-8 px-6">
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg shadow-sm">
                    <h3 className="text-lg font-semibold text-blue-700 mb-2">Important Learning Note</h3>
                    <p className="text-gray-700 text-sm mb-2">
                      To maximize your learning and prepare effectively for consulting interviews:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                      <li>Use only the provided calculator or a basic calculator</li>
                      <li>Solve problems independently to build mental math skills</li>
                      <li>Use "Show Solution" if stuck to learn the correct approach</li>
                      <li>Practice regularly to improve speed and accuracy</li>
                    </ul>
                    <p className="mt-2 text-sm text-gray-700 italic">
                      "The best way to prepare is to practice without external help - just like in real interviews!"
                    </p>
                  </div>
                </div>

                {/* Test Switch Button - Top */}
                <div className="flex justify-center mb-8">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleMathTestSwitch}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 text-lg font-semibold"
                  >
                    {activeMathTest === 1 ? "Do Another Test" : "Go to Test 1"}
                  </motion.button>
                </div>

                {/* Enhanced Floating Calculator */}
                <motion.div
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 100 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="fixed right-8 top-1/3 -translate-y-1/2 z-50 hidden md:block w-full max-w-md mx-auto md:mx-0"
                >
                  <div className="bg-white rounded-2xl shadow-xl p-6 w-full border border-blue-100">
                    <div className="text-center mb-3">
                      <span className="text-blue-600 font-medium text-lg">Your Friendly Calculator - Use Me! 😊</span>
                    </div>
                    
                    {/* Enhanced Display Area */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-2">
                      <div className="text-right text-sm text-gray-500 font-mono min-h-[1.5rem] overflow-x-auto">
                        {calculatorHistory.length > 0 && calculatorHistory[calculatorHistory.length - 1].expression}
                      </div>
                      <input
                        type="text"
                        value={calculatorExpression}
                        onChange={(e) => {
                          // Only allow valid calculator characters
                          const value = e.target.value.replace(/[^0-9+\-*/().%÷×]/g, '');
                          setCalculatorExpression(value);
                          setCalculatorValue(value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleCalculatorEquals();
                          }
                        }}
                        className="w-full text-right text-2xl font-mono bg-transparent border-none focus:ring-0 focus:outline-none overflow-x-auto"
                        placeholder="Enter calculation..."
                      />
                      <div className="text-right text-3xl font-mono font-semibold overflow-x-auto">
                        {calculatorValue}
                      </div>
                      {error && (
                        <div className="text-right text-sm text-red-600 font-medium min-h-[1.5rem]">
                          {error}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-5 gap-3">
                      {/* First Row */}
                      <button onClick={handleCalculatorClear} className="col-span-2 bg-red-100 text-red-600 rounded-lg p-3 hover:bg-red-200 transition-colors text-lg">Clear</button>
                      <button onClick={handleCalculatorBackspace} className="bg-red-100 text-red-600 rounded-lg p-3 hover:bg-red-200 transition-colors text-lg">⌫</button>
                      <button onClick={() => handleCalculatorBracket('(')} className="bg-blue-100 text-blue-600 rounded-lg p-3 hover:bg-blue-200 transition-colors text-lg">(</button>
                      <button onClick={() => handleCalculatorBracket(')')} className="bg-blue-100 text-blue-600 rounded-lg p-3 hover:bg-blue-200 transition-colors text-lg">)</button>

                      {/* Second Row */}
                      <button onClick={() => handleCalculatorInput('7')} className="bg-gray-100 text-gray-700 rounded-lg p-3 hover:bg-gray-200 transition-colors text-lg">7</button>
                      <button onClick={() => handleCalculatorInput('8')} className="bg-gray-100 text-gray-700 rounded-lg p-3 hover:bg-gray-200 transition-colors text-lg">8</button>
                      <button onClick={() => handleCalculatorInput('9')} className="bg-gray-100 text-gray-700 rounded-lg p-3 hover:bg-gray-200 transition-colors text-lg">9</button>
                      <button onClick={() => handleCalculatorOperation('÷')} className="bg-blue-100 text-blue-600 rounded-lg p-3 hover:bg-blue-200 transition-colors text-lg">÷</button>
                      <button onClick={handleCalculatorFraction} className="bg-blue-100 text-blue-600 rounded-lg p-3 hover:bg-blue-200 transition-colors text-lg">⅟x</button>

                      {/* Third Row */}
                      <button onClick={() => handleCalculatorInput('4')} className="bg-gray-100 text-gray-700 rounded-lg p-3 hover:bg-gray-200 transition-colors text-lg">4</button>
                      <button onClick={() => handleCalculatorInput('5')} className="bg-gray-100 text-gray-700 rounded-lg p-3 hover:bg-gray-200 transition-colors text-lg">5</button>
                      <button onClick={() => handleCalculatorInput('6')} className="bg-gray-100 text-gray-700 rounded-lg p-3 hover:bg-gray-200 transition-colors text-lg">6</button>
                      <button onClick={() => handleCalculatorOperation('×')} className="bg-blue-100 text-blue-600 rounded-lg p-3 hover:bg-blue-200 transition-colors text-lg">×</button>
                      <button onClick={handleCalculatorPercentage} className="bg-blue-100 text-blue-600 rounded-lg p-3 hover:bg-blue-200 transition-colors text-lg">%</button>

                      {/* Fourth Row */}
                      <button onClick={() => handleCalculatorInput('1')} className="bg-gray-100 text-gray-700 rounded-lg p-3 hover:bg-gray-200 transition-colors text-lg">1</button>
                      <button onClick={() => handleCalculatorInput('2')} className="bg-gray-100 text-gray-700 rounded-lg p-3 hover:bg-gray-200 transition-colors text-lg">2</button>
                      <button onClick={() => handleCalculatorInput('3')} className="bg-gray-100 text-gray-700 rounded-lg p-3 hover:bg-gray-200 transition-colors text-lg">3</button>
                      <button onClick={() => handleCalculatorOperation('-')} className="bg-blue-100 text-blue-600 rounded-lg p-3 hover:bg-blue-200 transition-colors text-lg">-</button>
                      <button onClick={handleCalculatorEquals} className="row-span-2 bg-blue-600 text-white rounded-lg p-3 hover:bg-blue-700 transition-colors text-lg">=</button>

                      {/* Fifth Row */}
                      <button onClick={() => handleCalculatorInput('0')} className="col-span-2 bg-gray-100 text-gray-700 rounded-lg p-3 hover:bg-gray-200 transition-colors text-lg">0</button>
                      <button onClick={() => handleCalculatorInput('.')} className="bg-gray-100 text-gray-700 rounded-lg p-3 hover:bg-gray-200 transition-colors text-lg">.</button>
                      <button onClick={() => handleCalculatorOperation('+')} className="bg-blue-100 text-blue-600 rounded-lg p-3 hover:bg-blue-200 transition-colors text-lg">+</button>
                    </div>
                  </div>
                </motion.div>

                <div className="max-w-full md:max-w-4xl mx-auto">
                  <div className="grid grid-cols-1 gap-4 md:gap-6">
                    {(activeMathTest === 1 ? testQuestions : mathTest2Questions).map((q) => (
                      <motion.div 
                        key={q.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative"
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center text-white font-medium shadow-md">
                            {q.id}
                          </div>
                          <div className="flex-grow">
                            <h3 className="text-lg font-medium mb-4">{q.question}</h3>
                            <div className="space-y-3">
                              <div className="flex gap-3">
                                <input
                                  type="text"
                                  value={testAnswers[q.id] || ''}
                                  onChange={(e) => handleTestInput(q.id, e.target.value)}
                                  onKeyDown={(e) => e.key === 'Enter' && handleTestSubmit(q.id)}
                                  className="flex-grow px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400"
                                  placeholder="Type your answer..."
                                />
                                <button
                                  onClick={() => handleTestSubmit(q.id)}
                                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                >
                                  Check
                                </button>
                              </div>
                              <div className="flex gap-2">
                                {testFeedback[q.id] === 'incorrect' && (
                                  <button
                                    onClick={() => handleTestTryAgain(q.id)}
                                    className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                  >
                                    Try Again
                                  </button>
                                )}
                                {testFeedback[q.id] === 'incorrect' && (
                                  <button
                                    onClick={() => handleTestReveal(q.id)}
                                    className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                  >
                                    Show Solution
                                  </button>
                                )}
                              </div>
                              {testFeedback[q.id] && (
                                <div className={`mt-4 p-3 rounded-lg ${
                                  testFeedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                                }`}>
                                  {testFeedback[q.id] === 'correct' ? 'Correct! 🎉' : 'Incorrect. Try again!'}
                                </div>
                              )}
                              {testReveal[q.id] && (
                                <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                                  <p className="text-blue-700">
                                    <span className="font-medium">Answer:</span> {q.correctAnswer}<br/>
                                    <span className="font-medium">Explanation:</span> {q.explanation}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Test Switch Button - Bottom */}
                  <div className="flex justify-center mt-8 mb-12">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleMathTestSwitch}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 text-lg font-semibold"
                    >
                      {activeMathTest === 1 ? "Do Another Test" : "Go to Test 1"}
                    </motion.button>
                  </div>

                  {/* Close Section Button */}
                  <div className="flex justify-center mt-12">
                    <button
                      onClick={() => setIsTestSectionOpen(false)}
                      className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 text-lg font-semibold"
                    >
                      Close Section
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Chart Types Section */}
        <section id="charts" ref={chartSectionRef} className="py-16 md:py-24 bg-gradient-to-b from-blue-50 via-white to-blue-50 text-center px-3 sm:px-2 md:px-6 relative">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold section-title tracking-tight mb-8 md:mb-12">Chart Types and How to Read Them</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-6xl mx-auto">
            {/* Bar Chart */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="chart-container group hover:scale-[1.03] bg-gradient-to-br from-blue-100 via-white to-blue-200 border-0 shadow-xl rounded-3xl p-10 flex flex-col gap-8 relative overflow-hidden"
            >
              <div className="absolute -top-6 -right-6 bg-gradient-to-br from-blue-400 to-blue-600 w-20 h-20 rounded-full opacity-20 z-0"></div>
              <div className="flex items-center gap-4 mb-4 z-10">
                <span className="text-4xl">📊</span>
                <h3 className="text-2xl font-bold text-blue-700">Bar Chart (Single Series)</h3>
              </div>
              <div className="z-10">
                <Bar data={barChartData} options={barChartOptions} />
                <div className="mt-6 text-left">
                  <p className="text-gray-700 text-lg mb-2">
                    <span className="font-semibold">Use:</span> Perfect for comparing different categories side by side. Great for showing which category is highest or lowest.
                  </p>
                  <p className="text-gray-600 mb-1">Example: Sales figures for four products - easily see which product sells best.</p>
                  <p className="text-blue-700 font-medium">How to Read: 
                    <ul className="list-disc list-inside mt-1 text-blue-600">
                      <li>Each bar represents one category (like a product)</li>
                      <li>The taller the bar, the higher the value</li>
                      <li>Compare heights to see which category is largest</li>
                      <li>Look at the Y-axis (vertical) for exact values</li>
                    </ul>
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Stacked Bar Chart */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="chart-container group hover:scale-[1.03] bg-gradient-to-br from-purple-100 via-white to-purple-200 border-0 shadow-xl rounded-3xl p-10 flex flex-col gap-8 relative overflow-hidden"
            >
              <div className="absolute -top-6 -right-6 bg-gradient-to-br from-purple-400 to-purple-600 w-20 h-20 rounded-full opacity-20 z-0"></div>
              <div className="flex items-center gap-4 mb-4 z-10">
                <span className="text-4xl">📚</span>
                <h3 className="text-2xl font-bold text-purple-700">Stacked Bar Chart</h3>
              </div>
              <div className="z-10">
                <Bar 
                  data={stackedBarData} 
                  options={{
                    ...barChartOptions,
                    scales: {
                      x: { stacked: true },
                      y: { stacked: true }
                    }
                  }} 
                />
                <div className="mt-6 text-left">
                  <p className="text-gray-700 text-lg mb-2">
                    <span className="font-semibold">Use:</span> Shows how different parts make up a whole, and how these parts change across categories. Great for seeing both total values and their components.
                  </p>
                  <p className="text-gray-600 mb-1">Example: Sales broken down into three sub-categories (like different product lines) across quarters - see both total sales and what makes up those sales.</p>
                  <p className="text-purple-700 font-medium">How to Read: 
                    <ul className="list-disc list-inside mt-1 text-purple-600">
                      <li>Each bar's total height shows the overall value</li>
                      <li>Different colors represent different components</li>
                      <li>Compare the colored segments to see component sizes</li>
                      <li>Look across bars to see how components change</li>
                    </ul>
                  </p>
                </div>
              </div>
            </motion.div>

            {/* 100% Stacked Bar Chart */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="chart-container group hover:scale-[1.03] bg-gradient-to-br from-green-100 via-white to-green-200 border-0 shadow-xl rounded-3xl p-10 flex flex-col gap-8 relative overflow-hidden"
            >
              <div className="absolute -top-6 -right-6 bg-gradient-to-br from-green-400 to-green-600 w-20 h-20 rounded-full opacity-20 z-0"></div>
              <div className="flex items-center gap-4 mb-4 z-10">
                <span className="text-4xl">💯</span>
                <h3 className="text-2xl font-bold text-green-700">100% Stacked Bar Chart</h3>
              </div>
              <div className="z-10">
                <Bar 
                  data={stackedBar100Data} 
                  options={{
                    ...barChartOptions,
                    scales: {
                      x: { stacked: true },
                      y: { 
                        stacked: true,
                        max: 100,
                        ticks: {
                          callback: function(value) {
                            return value + '%';
                          }
                        }
                      }
                    },
                    plugins: {
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            return context.dataset.label + ': ' + context.raw + '%';
                          }
                        }
                      }
                    }
                  }} 
                />
                <div className="mt-6 text-left">
                  <p className="text-gray-700 text-lg mb-2">
                    <span className="font-semibold">Use:</span> Shows the percentage breakdown of components across different categories. Perfect for comparing proportions rather than absolute values.
                  </p>
                  <p className="text-gray-600 mb-1">Example: Market share of products across regions - see what percentage each product holds in each region.</p>
                  <p className="text-green-700 font-medium">How to Read: 
                    <ul className="list-disc list-inside mt-1 text-green-600">
                      <li>Each bar always adds up to 100%</li>
                      <li>Different colors show different components</li>
                      <li>Compare the colored segments to see relative proportions</li>
                      <li>Look across bars to see how proportions change</li>
                      <li>Perfect for comparing market shares or distributions</li>
                    </ul>
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Pie Chart */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="chart-container group hover:scale-[1.03] bg-gradient-to-br from-yellow-100 via-white to-yellow-200 border-0 shadow-xl rounded-3xl p-10 flex flex-col gap-8 relative overflow-hidden"
            >
              <div className="absolute -top-6 -right-6 bg-gradient-to-br from-yellow-400 to-yellow-600 w-20 h-20 rounded-full opacity-20 z-0"></div>
              <div className="flex items-center gap-4 mb-4 z-10">
                <span className="text-4xl">🥧</span>
                <h3 className="text-2xl font-bold text-yellow-700">Pie Chart</h3>
              </div>
              <div className="z-10">
                <Pie data={pieChartData} />
                <div className="mt-6 text-left">
                  <p className="text-gray-700 text-lg mb-2">
                    <span className="font-semibold">Use:</span> Shows how a whole is divided into parts. Best when you have 2-5 categories and want to emphasize their relative proportions.
                  </p>
                  <p className="text-gray-600 mb-1">Example: Share of total sales by product - easily see which product contributes most to total sales.</p>
                  <p className="text-yellow-700 font-medium">How to Read: 
                    <ul className="list-disc list-inside mt-1 text-yellow-600">
                      <li>Each slice represents one category</li>
                      <li>Larger slices mean larger proportions</li>
                      <li>The whole pie equals 100%</li>
                      <li>Compare slice sizes to see relative importance</li>
                      <li>Look at the legend to identify each slice</li>
                    </ul>
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Line Chart */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="chart-container group hover:scale-[1.03] bg-gradient-to-br from-pink-100 via-white to-pink-200 border-0 shadow-xl rounded-3xl p-10 flex flex-col gap-8 relative overflow-hidden"
            >
              <div className="absolute -top-6 -right-6 bg-gradient-to-br from-pink-400 to-pink-600 w-20 h-20 rounded-full opacity-20 z-0"></div>
              <div className="flex items-center gap-4 mb-4 z-10">
                <span className="text-4xl">📈</span>
                <h3 className="text-2xl font-bold text-pink-700">Line Chart</h3>
              </div>
              <div className="z-10">
                <Line data={lineChartData} />
                <div className="mt-6 text-left">
                  <p className="text-gray-700 text-lg mb-2">
                    <span className="font-semibold">Use:</span> Shows how values change over time. Perfect for spotting trends, patterns, and changes in data.
                  </p>
                  <p className="text-gray-600 mb-1">Example: Monthly revenue - see how sales grow or decline over time.</p>
                  <p className="text-pink-700 font-medium">How to Read: 
                    <ul className="list-disc list-inside mt-1 text-pink-600">
                      <li>Follow the line from left to right to see changes over time</li>
                      <li>Upward slope means values are increasing</li>
                      <li>Downward slope means values are decreasing</li>
                      <li>Steep slopes show rapid changes</li>
                      <li>Flat lines show no change</li>
                      <li>Look for patterns like seasonal changes or trends</li>
                    </ul>
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Scatter Plot */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="chart-container group hover:scale-[1.03] bg-gradient-to-br from-cyan-100 via-white to-cyan-200 border-0 shadow-xl rounded-3xl p-10 flex flex-col gap-8 relative overflow-hidden"
            >
              <div className="absolute -top-6 -right-6 bg-gradient-to-br from-cyan-400 to-cyan-600 w-20 h-20 rounded-full opacity-20 z-0"></div>
              <div className="flex items-center gap-4 mb-4 z-10">
                <span className="text-4xl">🔵</span>
                <h3 className="text-2xl font-bold text-cyan-700">Scatter Plot</h3>
              </div>
              <div className="z-10">
                <Scatter 
                  data={scatterData}
                  options={{
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Study Hours'
                        }
                      },
                      y: {
                        title: {
                          display: true,
                          text: 'Test Score'
                        }
                      }
                    }
                  }}
                />
                <div className="mt-6 text-left">
                  <p className="text-gray-700 text-lg mb-2">
                    <span className="font-semibold">Use:</span> Shows the relationship between two different measurements. Great for finding patterns and correlations in your data.
                  </p>
                  <p className="text-gray-600 mb-1">Example: Hours studied vs test scores - see if more study time leads to better scores.</p>
                  <p className="text-cyan-700 font-medium">How to Read: 
                    <ul className="list-disc list-inside mt-1 text-cyan-600">
                      <li>Each dot represents one observation or data point</li>
                      <li>X-axis shows one measurement (like study hours)</li>
                      <li>Y-axis shows another measurement (like test scores)</li>
                      <li>Look for patterns in how the dots are arranged</li>
                      <li>Dots going up and right suggest positive correlation</li>
                      <li>Dots going down and right suggest negative correlation</li>
                      <li>Scattered dots suggest no clear relationship</li>
                    </ul>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Add Practice Button after Chart Section */}
          <div className="mt-16 text-center">
            <p className="text-xl text-gray-600 mb-6">Ready to apply your chart interpretation skills?</p>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/redrock')}
              className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
            >
              Practice in RedRockCases
              <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
            </motion.button>
          </div>
        </section>

        {/* Test Your Learning: Charts & Graphs Section */}
        <div id="chart-test" className="mt-10 md:mt-20">
          <div className="flex items-center justify-center gap-2 md:gap-4 mb-6 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-bold section-title tracking-tight">Test Your Learning: Charts & Graphs</h2>
            <div className="relative">
              <div className="absolute -top-6 md:-top-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                <div className="flex items-center gap-1 md:gap-2">
                  <div className="h-0.5 w-4 md:w-8 bg-gradient-to-r from-transparent to-blue-500"></div>
                  <span className="text-blue-500 font-medium tracking-wider animate-pulse text-xs md:text-base">PRESS ME</span>
                  <div className="h-0.5 w-4 md:w-8 bg-gradient-to-l from-transparent to-blue-500"></div>
                </div>
              </div>
              <motion.button 
                whileHover={{ scale: 1.1, rotate: 180 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsChartTestSectionOpen(!isChartTestSectionOpen)}
                className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-gradient-to-r from-blue-600 to-blue-400 flex items-center justify-center text-white shadow-lg transform transition-transform duration-200 group"
              >
                <span className="text-lg md:text-xl transform group-hover:rotate-12 transition-transform duration-200">🔌</span>
              </motion.button>
            </div>
          </div>
          <AnimatePresence>
            {isChartTestSectionOpen && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="overflow-hidden relative"
              >
                {/* Test Switch Button - Top */}
                <div className="flex justify-center mb-4 md:mb-8">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleChartTestSwitch}
                    className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 text-base md:text-lg font-semibold"
                  >
                    {activeChartTest === 1 ? "Do Another Test" : "Go to Test 1"}
                  </motion.button>
                </div>
                <div className="max-w-full md:max-w-4xl mx-auto">
                  <div className="grid grid-cols-1 gap-3 md:gap-6">
                    {(activeChartTest === 1 ? chartTestQuestions : chartTest2Questions).map((q) => (
                      <div key={q.id} className="bg-white p-3 md:p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative overflow-x-auto max-w-full">
                        <div className="flex items-start gap-2 md:gap-4">
                          <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center text-white font-medium shadow-md text-sm md:text-base">
                            {q.id}
                          </div>
                          <div className="flex-grow min-w-0">
                            <h3 className="text-base md:text-lg font-medium mb-2 md:mb-4 break-words max-w-xs mx-auto md:max-w-none md:mx-0">{q.question}</h3>
                            {q.chart && (
                              <div className="text-lg md:text-3xl mb-1 md:mb-2 overflow-x-auto max-w-full">{q.chart}</div>
                            )}
                            <div className="space-y-2 md:space-y-3">
                              <div className="flex flex-col gap-1 md:gap-2">
                                {q.options.map((opt) => (
                                  <button
                                    key={opt.value}
                                    onClick={() => handleChartTestSelect(q.id, opt.value)}
                                    className={`px-3 md:px-4 py-2 rounded-lg border transition-all duration-200 text-left flex items-center gap-2 text-xs md:text-base
                                      ${chartTestAnswers[q.id] === opt.value
                                        ? (chartTestFeedback[q.id] === 'correct' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-red-100 border-red-400 text-red-700')
                                        : 'bg-gradient-to-r from-blue-100 to-blue-50 text-blue-700 border-blue-200 hover:bg-blue-200'}
                                    `}
                                    disabled={!!chartTestFeedback[q.id]}
                                  >
                                    {opt.emoji && <span className="text-lg md:text-2xl">{opt.emoji}</span>}
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                              <div className="flex gap-1 md:gap-2">
                                {chartTestFeedback[q.id] === 'incorrect' && (
                                  <button
                                    onClick={() => handleChartTestTryAgain(q.id)}
                                    className="px-2 md:px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 text-xs md:text-base"
                                  >
                                    Try Again
                                  </button>
                                )}
                                {chartTestFeedback[q.id] === 'incorrect' && (
                                  <button
                                    onClick={() => handleChartTestReveal(q.id)}
                                    className="px-2 md:px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 text-xs md:text-base"
                                  >
                                    Show Solution
                                  </button>
                                )}
                              </div>
                              {chartTestFeedback[q.id] && (
                                <div className={`mt-2 md:mt-4 p-2 md:p-3 rounded-lg ${
                                  chartTestFeedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                                } text-xs md:text-base`}>
                                  {chartTestFeedback[q.id] === 'correct' ? 'Correct! 🎉' : 'Incorrect. Try again!'}
                                </div>
                              )}
                              {chartTestReveal[q.id] && (
                                <div className="mt-1 md:mt-2 p-2 md:p-3 bg-blue-50 rounded-lg text-xs md:text-base">
                                  <p className="text-blue-700">
                                    <span className="font-medium">Answer:</span> {q.options.find(opt => opt.value === q.correctAnswer)?.label || q.correctAnswer}<br/>
                                    <span className="font-medium">Explanation:</span> {q.explanation}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Test Switch Button - Bottom */}
                <div className="flex justify-center mt-4 md:mt-8 mb-6 md:mb-12">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleChartTestSwitch}
                    className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 text-base md:text-lg font-semibold"
                  >
                    {activeChartTest === 1 ? "Do Another Test" : "Go to Test 1"}
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
} 
